import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import type { JointMotion } from "../components";
import { BabylonState } from "../state";

/**
 * An axis as the vizstate authors one: `[x, y, z]`, not necessarily unit length.
 *
 * A plain tuple rather than a Babylon `Vector3` because this crosses the React
 * boundary — it is what `schema.Joint.axis` already holds, so a caller hands
 * over the axis the model file declared without converting first.
 */
export type JointAxis = readonly [number, number, number];

/** One joint's live value, as a caller supplies it. */
export interface JointPoseInput {
  /** Whether driving this joint turns its link or slides it along the axis. */
  motion: JointMotion;
  /** The axis it moves about or along, in the driven entity's parent frame. */
  axis: JointAxis;
  /** Radians for a `rotate`, metres for a `slide`. */
  value: number;
}

/**
 * Moves one joint to `pose`, taking effect on the next frame.
 *
 * A triggered system, in the `onEvent` sense the README describes: the React
 * layer calls it, and it mutates component state directly rather than declaring
 * anything. That is the whole point of it existing. A joint value pushed through
 * the panel state instead reaches `updateEntities` as a changed `Rotation` spec,
 * and the only thing `updateEntities` can do with a changed spec is dispose the
 * component and recreate it at identity, marked stale for the next
 * `reloadStaleComponents` poll — three frames of the joint and everything below
 * it snapped back to the rest pose, on every tick of whatever is driving it.
 *
 * Writing an existing pose mutates it in place, so driving a joint continuously
 * never replaces a component and never invalidates a cache.
 *
 * Unknown entities are ignored. The scene the ids came from can be replaced
 * asynchronously, and a write that arrives against the previous one is not worth
 * failing over.
 *
 * @param entityId the entity the joint moves — a joint's **child link**, which
 *   is what `schema.Joint.drives` names, never the joint's own node
 */
export function onSetJointPose(
  state: BabylonState,
  entityId: schema.EntityID,
  pose: JointPoseInput,
) {
  const entity = state.entities[entityId];
  if (!entity) {
    return;
  }

  const existing = entity.jointPose;
  if (existing) {
    existing.motion = pose.motion;
    existing.axis.set(pose.axis[0], pose.axis[1], pose.axis[2]);
    existing.value = pose.value;
    return;
  }

  entity.jointPose = {
    motion: pose.motion,
    axis: new babylon.Vector3(pose.axis[0], pose.axis[1], pose.axis[2]),
    value: pose.value,
    // Pre-allocated for `poseJoints` to write into; the values here are never
    // read, since that system rewrites both before anything composes them.
    quaternion: babylon.Quaternion.Identity(),
    displacement: babylon.Vector3.Zero(),
  };
}

/**
 * Returns the joint to the transform its spec declares.
 *
 * Dropping the component *is* the reset: the spec has held the rest pose all
 * along — nothing overwrote it — so the next frame puts the entity back where
 * the model file said it sits, both in orientation and in offset.
 */
export function onClearJointPose(
  state: BabylonState,
  entityId: schema.EntityID,
) {
  const entity = state.entities[entityId];
  if (entity) {
    delete entity.jointPose;
  }
}

/**
 * The value a joint is being driven to, or `undefined` if nothing is driving it.
 *
 * The read-back for the same seam `onSetJointPose` writes through, so a caller
 * can reconcile a pose against what the scene already holds instead of
 * rewriting every joint it knows about.
 */
export function jointPoseValue(
  state: BabylonState,
  entityId: schema.EntityID,
): number | undefined {
  return state.entities[entityId]?.jointPose?.value;
}

/**
 * Moves one joint of an entity's `model` to `value`, taking effect on the next
 * frame.
 *
 * The model-scoped counterpart of {@link onSetJointPose}, and a triggered system
 * for the same reason: a joint value is a live quantity, not a declaration. The
 * declarative channel for a model's joints is `mesh.jointValues`, which is a
 * *spec* — `updateEntities` reconciles it into `jointCaches` and
 * `reloadStaleJointValues` resolves those on its 50ms poll. Writing a slider's
 * value through it would restale the cache on every input event and leave the
 * joint reading nothing until the next poll: at a 16ms frame, roughly three
 * frames of that joint and its whole subtree back at the rest pose, per tick.
 * This writes one number into a record the render loop already reads, so nothing
 * is disposed, nothing is refetched, and no frame passes through the rest pose.
 *
 * A joint is addressed by the name the model file gave it — the same key
 * `spec.joints`, `spec.jointValues` and `jointCaches` all use — so a caller
 * needs to know nothing about which node the joint moves or how it moves it.
 * `poseJoints` reads the type and the axis off the joint's own declaration.
 *
 * The value takes precedence over whatever that joint's binding resolved to, in
 * the same sense a `JointPose` overrides a spec-derived rotation. Joints that
 * are not named here keep their bound values, so posing one joint of a bound
 * model leaves the rest playing back.
 *
 * A joint coupled by `mimic` cannot be driven this way: its value is computed
 * from its driver's, which is the articulation's claim rather than the caller's.
 * Drive the driver.
 *
 * Entities that are absent, or that draw something other than a model, are
 * ignored — the scene these ids come from is replaced asynchronously, and a
 * write against the previous one is not worth failing over. `value` is in the
 * joint's own units: radians about the declared axis for a joint that turns,
 * metres along it for one that slides.
 */
export function onSetModelJointPose(
  state: BabylonState,
  entityId: schema.EntityID,
  joint: string,
  value: number,
) {
  const mesh = state.entities[entityId]?.mesh;
  if (!mesh?.model) {
    return;
  }
  // Mutated in place, never replaced: `poseJoints` reads this record every
  // frame, and swapping it out is the class of thing this API exists to avoid.
  const poses = (mesh.jointPoses ??= {});
  poses[joint] = value;
}

/**
 * Returns one of a model's joints to whatever its spec says it is at.
 *
 * Dropping the value *is* the reset, as it is for an entity's `JointPose`:
 * nothing here ever wrote to `jointValues` or to its cache, so the next frame
 * poses the joint from its binding again — or, if nothing binds it, at the zero
 * pose the model file was exported in.
 */
export function onClearModelJointPose(
  state: BabylonState,
  entityId: schema.EntityID,
  joint: string,
) {
  const poses = state.entities[entityId]?.mesh?.jointPoses;
  if (poses) {
    delete poses[joint];
  }
}

/**
 * The value one of a model's joints is being driven to, or `undefined` if
 * nothing is driving it and it therefore follows its binding.
 *
 * The read-back for the seam {@link onSetModelJointPose} writes through, so a
 * caller can reconcile a whole pose against what the scene already holds instead
 * of rewriting every joint it knows about on every input event.
 */
export function modelJointPoseValue(
  state: BabylonState,
  entityId: schema.EntityID,
  joint: string,
): number | undefined {
  return state.entities[entityId]?.mesh?.jointPoses?.[joint];
}
